// src/components/Header.tsx
import React from 'react';
import { Layout, Menu, Dropdown, Avatar, Space, Typography, message } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

/**
 * Header component displayed at the top of the authenticated layout.
 * It contains a logo, navigation links and a user avatar with a dropdown menu.
 */
const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, authState } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Log the error and inform the user without exposing internal details.
      console.error('Logout failed:', error);
      // Ant Design message provides a non‑blocking UI notification.
      message.error('Unable to log out. Please try again.');
    } finally {
      // After logout (or on error), redirect to login page to avoid stale UI.
      navigate('/login', { replace: true });
    }
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'settings') {
      navigate('/settings');
    } else if (key === 'logout') {
      handleLogout();
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        Profile
      </Menu.Item>
      <Menu.Item key="settings" icon={<SettingOutlined />}>
        Settings
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  // Define navigation items for the main sections of the app
  const navItems = [
    { key: '/map', label: 'Map' },
    { key: '/forms', label: 'Forms' },
    { key: '/records', label: 'Records' },
    { key: '/admin', label: 'Admin' },
    { key: '/offline', label: 'Offline' },
  ];

  return (
    <AntHeader style={{ background: '#fff', padding: '0 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {/* Logo / App name */}
        <Link to="/" style={{ marginRight: 24, fontSize: 20, fontWeight: 'bold' }}>
          Pivot
        </Link>
        {/* Navigation menu */}
        <Menu
          mode="horizontal"
          selectedKeys={[navItems.find(item => location.pathname.startsWith(item.key))?.key || location.pathname]}
          items={navItems.map((item) => ({
            key: item.key,
            label: <Link to={item.key}>{item.label}</Link>,
          }))}
          style={{ flex: 1 }}
        />
        {/* User avatar dropdown */}
        <Space>
          <Text>{authState?.user?.fullName || authState?.user?.email || 'User'}</Text>
          <Dropdown
            overlay={menu}
            trigger={['click']}
            placement="bottomRight"
            aria-label="User menu"
          >
            <Avatar style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </div>
    </AntHeader>
  );
};

export default Header;